import { Field, HStack, Input, InputGroup, Menu, Popover, Portal } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import DayPicker, { DateUtils } from 'react-day-picker'
import 'react-day-picker/lib/style.css'
import { IoCalendarOutline, IoClose } from 'react-icons/io5'
import MaskedInput from 'react-text-mask'
import { useColorMode } from 'uikit/ColorMode/ColorMode'
import { FormLabel } from '../Form/FormLabel'
import { FIRST_DAY_OF_WEEK, LABELS, MONTHS, WEEKDAYS_LONG, WEEKDAYS_SHORT, locale } from './config'
import { getDateReadFormat, getDateToPicker, isValidDate } from './dateFormatting'
import './style.scss'

const RANGE_SEPARATOR = '-'

export const InputDate = ({
  name,
  onChange,
  placement = 'top',
  hideClearButton,
  defaultValue,
  value,
  label,
  dayPickerProps = {},
  isRangePick,
  isInvalid = false,
  withDialog = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDay, setSelectedDay] = useState(defaultValue ? defaultValue : value)
  const [inputState, setInputState] = useState(defaultValue ? defaultValue : value)

  const [selectedRange, setSelectedRange] = useState({
    from: undefined,
    to: undefined,
  })

  const handleInputBlur = (e) => {
    const value = e.target.value

    if (isRangePick) {
      const range = value.split(RANGE_SEPARATOR)
      const from = range[0] ? getDateToPicker(range[0]) : undefined
      const to = range[1] ? getDateToPicker(range[1]) : undefined

      setSelectedRange({ from, to })
    } else if (isValidDate(value)) {
      setSelectedDay(getDateToPicker(value))
    } else {
      setSelectedDay('')
      setInputState('')
    }
  }

  const clear = () => {
    setSelectedRange({
      from: undefined,
      to: undefined,
    })
    setSelectedDay(undefined)
    setInputState(undefined)
  }

  useEffect(() => {
    if (onChange) {
      if (isRangePick) {
        onChange(selectedRange)
      } else if (selectedDay !== '' && selectedDay !== value) {
        const readDate = selectedDay && getDateReadFormat(selectedDay)

        onChange(readDate)
      }
    }
  }, [selectedDay, selectedRange])

  useEffect(() => {
    if (isRangePick) {
      if (value) {
        const fromStr = value.from ? getDateReadFormat(value.from) : ''
        const toStr = value.to ? getDateReadFormat(value.to) : ''
        const inputStateNew = toStr ? [fromStr, toStr].join(RANGE_SEPARATOR) : fromStr

        setSelectedRange(value)
        setInputState(inputStateNew)
      }
    } else {
      const day = value && isValidDate(value) ? getDateToPicker(value) : ''

      if (day === '') {
        setSelectedDay(undefined)
        setInputState(undefined)
      }
      if (day !== selectedDay) {
        setSelectedDay(day)
        setInputState(value)
      }
    }
  }, [value])

  const inputMask = [
    /[0-9]/,
    /[0-9]/,
    '.',
    /[0-9]/,
    /[0-9]/,
    '.',
    /[0-9]/,
    /[0-9]/,
    /[0-9]/,
    /[0-9]/,
  ]
  const rangeInputMask = [...inputMask, RANGE_SEPARATOR, ...inputMask]

  const hasSelected = inputState
  const selectedDays = isRangePick ? [selectedRange.from, selectedRange] : selectedDay

  const rangeDayPickerProps = {
    modifiers: { start: selectedRange.from, end: selectedRange.to },
  }

  return (
    <Field.Root invalid={isInvalid}>
      {label && <FormLabel>{label}</FormLabel>}
      <InputGroup
        endElement={
          <HStack>
            {hasSelected && !hideClearButton && (
              <IoClose
                size="20px"
                color="gray.600"
                mr={2}
                onClick={clear}
                cursor="pointer"
                transition="0.2s"
                _hover={{
                  color: 'gray.500',
                }}
              />
            )}
            <CalendarButton
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              selectedRange={selectedRange}
              placement={placement}
              isRangePick={isRangePick}
              selectedDays={selectedDays}
              rangeDayPickerProps={rangeDayPickerProps}
              dayPickerProps={dayPickerProps}
              setSelectedDay={setSelectedDay}
              setInputState={setInputState}
              setSelectedRange={setSelectedRange}
              withDialog={withDialog}
            />
          </HStack>
        }
      >
        <MaskedInput
          mask={isRangePick ? rangeInputMask : inputMask}
          id={name}
          value={inputState || ''}
          render={(ref, props) => {
            return (
              <Input
                ref={ref}
                id={name}
                {...props}
                borderColor={isInvalid ? 'red.500' : undefined}
                _focus={{
                  ...(props._focus || {}),
                  ...(isInvalid && { borderColor: 'red.500' }),
                }}
              />
            )
          }}
          onChange={(e) => setInputState(e.target.value)}
          onBlur={handleInputBlur}
          autoComplete="off"
          {...props}
        />
      </InputGroup>
    </Field.Root>
  )
}

const CalendarButton = ({
  isOpen,
  setIsOpen,
  placement,
  isRangePick,
  selectedDays,
  selectedRange,
  rangeDayPickerProps,
  dayPickerProps,
  setSelectedDay,
  setInputState,
  setSelectedRange,
  withDialog,
}) => {
  const { colorMode } = useColorMode()
  const initialFocusRef = useRef()

  const handleDayClick = (day, { selected, disabled }) => {
    if (disabled) {
      return
    }

    if (isRangePick) {
      const range = DateUtils.addDayToRange(day, selectedRange)
      const fromStr = range.from ? getDateReadFormat(range.from) : ''
      const toStr = range.to ? getDateReadFormat(range.to) : ''
      const inputStateNew = toStr ? [fromStr, toStr].join(RANGE_SEPARATOR) : fromStr

      setSelectedRange(range)
      setInputState(inputStateNew)
    } else if (!selected) {
      const dateReadFormat = getDateReadFormat(day)
      setSelectedDay(day)
      setInputState(dateReadFormat)
    }
  }

  return (
    <Menu.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)} positioning={{ placement }}>
      <Menu.Trigger color="gray.300" asChild>
        <IoCalendarOutline
          size="18px"
          transition="0.2s"
          cursor="pointer"
          mr={1}
          onClick={() => setIsOpen((state) => !state)}
          _hover={{
            color: 'gray.300',
          }}
        />
      </Menu.Trigger>
      <Portal disabled={withDialog}>
        <Menu.Positioner>
          <Menu.Content width="300px" padding="8px 8px 6px 8px">
            <DayPicker
              className={colorMode === 'light' ? 'UIDatePickerLight' : 'UIDatePickerDark'}
              locale={locale}
              months={MONTHS[locale]}
              weekdaysLong={WEEKDAYS_LONG[locale]}
              weekdaysShort={WEEKDAYS_SHORT[locale]}
              firstDayOfWeek={FIRST_DAY_OF_WEEK[locale]}
              labels={LABELS[locale]}
              numberOfMonths={1}
              onDayClick={handleDayClick}
              selectedDays={selectedDays}
              {...(isRangePick && rangeDayPickerProps)}
              {...dayPickerProps}
              ref={initialFocusRef}
            />
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}
